/**
 * Phase 8 — Reactive events.
 *
 * Random galactic incidents that perturb the player's station for a window
 * of ticks: stellar storms damp power; pirate raids steal in-transit cargo;
 * supply booms warp market prices. Events fire off a server-side scheduler
 * and broadcast as part of every world snapshot so the client UI stays
 * in lock-step.
 *
 * Three kinds shipped in the first slice — each chosen to touch a *different*
 * subsystem so the integration story stays clean:
 *
 *   stellar-storm  — global throttle dampener (power pipeline)
 *   pirate-raid    — one-shot drones-in-transit cargo loss (drone state)
 *   supply-boom    — per-resource price multiplier (market tracker)
 *
 * P10 v1.5 added three faction-flavored kinds. Each one carries a
 * `factionId` payload and warps the contract economy of that faction
 * in the personal pool for the duration:
 *
 *   trade-embargo  — issuer drops out of the personal pool issuer
 *                    rotation; the embargoed faction stops issuing
 *                    new offers entirely until the event lifts
 *   faction-bonus  — every contract from that faction pays ×1.5
 *   rival-undercut — every contract from that faction pays ×0.7
 *                    (the rival is flooding the market)
 *
 * Per-charter scope: events live on `World.events`, so each charter
 * sees their own roll. The shared Bid Board (process-global) is
 * unaffected in v1; routing global events into the bid board is a
 * deferred v2 enhancement.
 *
 * Each event payload carries enough data for the UI to render its row
 * without needing follow-up lookups (no resource ids, no ratio decoding).
 *
 * Why not callbacks/effects-as-functions? Events round-trip through the
 * wire and through persistence — pure data only. The server's effect
 * appliers switch on `kind` and read the typed payload. Adding a new event
 * means adding a kind, a payload variant, an applier branch, and a UI row.
 */
/**
 * Catalog of events the scheduler can pick from. Keeping it separate from
 * `EventPayload` lets the scheduler roll a kind via weight, then build a
 * concrete payload (potentially with random parameters like resource id).
 */
export const EVENT_TEMPLATES = {
    'stellar-storm': {
        kind: 'stellar-storm',
        title: 'Stellar Storm',
        description: 'Solar interference dampens the grid. Machines run slower until it passes.',
        severity: 'bad',
        durationTicks: 60 * 5, // 60 seconds at 5 Hz
        weight: 3,
    },
    'pirate-raid': {
        kind: 'pirate-raid',
        title: 'Pirate Raid',
        description: 'Raiders intercepted in-transit drones — half their cargo is gone.',
        severity: 'bad',
        durationTicks: 1, // one-shot — only the start moment matters
        weight: 1,
    },
    'supply-boom': {
        kind: 'supply-boom',
        title: 'Supply Boom',
        description: 'Demand surge — the affected good fetches a premium until it cools.',
        severity: 'good',
        durationTicks: 90 * 5, // 90 seconds
        weight: 2,
    },
    // P10 v1.5 — faction events. Combined weight (3) sits at ~30% of the
    // total spawn pool so faction-flavored events land regularly without
    // crowding out the original three.
    'trade-embargo': {
        kind: 'trade-embargo',
        title: 'Trade Embargo',
        description: "A faction has pulled their signature contracts and frozen their bid-board offers. Other patrons fill the gap.",
        severity: 'bad',
        durationTicks: 150 * 5, // 2.5 minutes
        weight: 1,
    },
    'faction-bonus': {
        kind: 'faction-bonus',
        title: 'Faction Bonus',
        description: "A faction is paying a premium on their signature contracts to clear backlog. Stack the rep boost.",
        severity: 'good',
        durationTicks: 120 * 5, // 2 minutes
        weight: 1,
    },
    'rival-undercut': {
        kind: 'rival-undercut',
        title: 'Rival Undercut',
        description: "A rival corp is flooding the market with substitute goods. Affected faction's signature payouts dip until the surge passes.",
        severity: 'bad',
        durationTicks: 180 * 5, // 3 minutes
        weight: 1,
    },
};
export const ALL_EVENT_KINDS = Object.keys(EVENT_TEMPLATES);
/** Ratio of total weight one kind represents. Useful for the admin panel. */
export function totalEventWeight() {
    let s = 0;
    for (const k of ALL_EVENT_KINDS)
        s += EVENT_TEMPLATES[k].weight;
    return s;
}
/**
 * Tunable knobs (also exposed via admin tuning keys). Defaults are tuned for
 * 5 Hz / 3-minute contracts: a fresh charter sees an event every ~3-5 minutes
 * on average.
 */
export const DEFAULT_EVENT_SPAWN_PER_TICK = 0.001;
export const DEFAULT_EVENT_DURATION_JITTER = 0.25; // ±25% on the catalog duration
export const DEFAULT_MAX_ACTIVE_EVENTS = 2;
//# sourceMappingURL=events.js.map